import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Server, Link, AlertTriangle, Loader2, Zap, Globe, Crown, ArrowUpRight, RefreshCw, Activity, BarChart3, ChevronDown, Trash, Clock } from 'lucide-react';

interface DashboardStats {
  totalServers: number;
  activeServers: number;
  totalPages: number;
  activeLinks: number;
  recentActivations: number;
}

interface ServerStatus {
  id: number;
  name: string;
  host: string;
  status: 'online' | 'offline' | 'checking';
  lastCheck: string;
  activeLinks: number;
  uptime: string;
}

interface QuickStats {
  totalActivations: number;
  activePages: number;
  cleanedFiles: number;
  lastCleanup: string | null;
}

interface SubscriptionInfo {
  plan: string;
  expired: boolean;
  limits: { servers: number | string; domains: number | string };
  usage: { servers: number; domains: number };
}

const DashboardPanel: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalServers: 0,
    activeServers: 0,
    totalPages: 0,
    activeLinks: 0,
    recentActivations: 0
  });
  const [servers, setServers] = useState<ServerStatus[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedServer, setExpandedServer] = useState<number | null>(null);
  const [serverQuickStats, setServerQuickStats] = useState<Record<number, QuickStats>>({});
  const [loadingStats, setLoadingStats] = useState<number | null>(null);

  const handleServerClick = async (serverId: number) => {
    if (expandedServer === serverId) {
      setExpandedServer(null);
      return;
    }

    setExpandedServer(serverId);

    // Load quick stats if not already loaded
    if (!serverQuickStats[serverId]) {
      setLoadingStats(serverId);
      try {
        const res = await fetch(`/api/servers/${serverId}/stats`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) {
          setServerQuickStats(prev => ({
            ...prev,
            [serverId]: {
              totalActivations: data.stats.totalActivations,
              activePages: data.stats.activePages,
              cleanedFiles: data.stats.cleanedFiles,
              lastCleanup: data.stats.lastCleanup
            }
          }));
        }
      } catch { }
      setLoadingStats(null);
    }
  };

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError('');

      const [statsRes, serversRes, subRes] = await Promise.all([
        fetch('/api/dashboard/stats', { credentials: 'include' }),
        fetch('/api/dashboard/servers', { credentials: 'include' }),
        fetch('/api/auth/subscription', { credentials: 'include' })
      ]);

      const statsData = await statsRes.json();
      const serversData = await serversRes.json();
      const subData = await subRes.json();

      if (statsData.success) setStats(statsData.stats);
      if (serversData.success) setServers(serversData.servers);
      if (subData.success) setSubscription(subData.subscription);

    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && stats.totalServers === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const statItems = [
    { label: 'Total Servers', value: stats.totalServers, icon: Server },
    { label: 'Active Servers', value: stats.activeServers, icon: Zap },
    { label: 'Total Pages', value: stats.totalPages, icon: Globe },
    { label: 'Active Links', value: stats.activeLinks, icon: Link },
    { label: 'Last 24h', value: stats.recentActivations, icon: BarChart3 },
  ];

  return (
    <div className="w-full min-h-full animate-fade-in grid-bg">
      {/* Header */}
      <div className="flex items-center justify-between mb-12">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Dash<span className="gradient-text">board</span></h1>
          <p className="text-white/50">Overview of your infrastructure</p>
        </div>
        <button
          onClick={loadDashboardData}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 text-white/70 hover:text-white transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span className="text-sm">Refresh</span>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 rounded-lg flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {/* Subscription Banner */}
      {subscription && subscription.plan !== 'Admin' && (subscription.expired || subscription.plan === 'Free') && (
        <div className="mb-8 p-5 rounded-lg bg-white/[0.02]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Crown className={`w-5 h-5 ${subscription.expired ? 'text-red-400' : 'text-purple-400'}`} />
              <div>
                <span className="text-white font-medium">{subscription.plan} Plan</span>
                {subscription.expired && <span className="text-red-400 ml-2">(Expired)</span>}
                <span className="text-white/40 ml-4 text-sm">
                  {subscription.usage.servers}/{subscription.limits.servers} servers • {subscription.usage.domains}/{subscription.limits.domains} domains
                </span>
              </div>
            </div>
            <a
              href="https://ikcore.cloud/pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg text-sm font-medium hover:bg-white/90 transition-colors"
            >
              Upgrade
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}

      {/* Stats Grid - with soft borders */}
      <div className="grid grid-cols-5 gap-6 mb-10">
        {statItems.map((item, i) => (
          <div key={i} className="p-5 rounded-lg bg-white/[0.02] border border-white/10 glow-card animate-stagger">
            <div className="flex items-center gap-2 mb-3">
              <item.icon className="w-4 h-4 text-white/40" />
              <span className="text-sm text-white/40">{item.label}</span>
            </div>
            <p className="text-3xl font-semibold text-white">{item.value}</p>
          </div>
        ))}
      </div>

      {/* Server Status */}
      <div className="rounded-lg bg-white/[0.02] border border-white/10 animate-slide-up glow-card">
        <div className="p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className="w-4 h-4 text-white/40" />
            <span className="text-white font-medium">Server Status</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-4 text-sm text-white/40">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-400"></span>
                {servers.filter(s => s.status === 'online').length} online
              </span>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-400"></span>
                {servers.filter(s => s.status === 'offline').length} offline
              </span>
            </div>
            {servers.length > 0 && (
              <button
                onClick={() => navigate('/servers')}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-all shadow-lg"
                style={{
                  backgroundColor: 'var(--accent-primary)',
                  boxShadow: '0 4px 14px rgba(var(--accent-primary-rgb), 0.4)'
                }}
              >
                View all
                <ArrowUpRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {servers.length === 0 ? (
          <div className="p-12 text-center">
            <Server className="w-10 h-10 text-white/20 mx-auto mb-4" />
            <p className="text-white/60 mb-1">No servers configured</p>
            <p className="text-white/30 text-sm">Add servers to monitor their status</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {servers.map((server) => {
              const isExpanded = expandedServer === server.id;
              const quickStats = serverQuickStats[server.id];
              const isLoadingThisStats = loadingStats === server.id;

              return (
                <div key={server.id}>
                  <div
                    className="px-5 py-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors cursor-pointer"
                    onClick={() => handleServerClick(server.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-2 h-2 rounded-full ${server.status === 'online' ? 'bg-green-400' : 'bg-red-400'}`} />
                      <div>
                        <span className="text-white font-medium">{server.name}</span>
                        <span className="text-white/30 ml-3 text-sm">{server.host}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <span className="text-white/40">{server.activeLinks} links</span>
                      <span className="text-white/40">{server.uptime}</span>
                      <span className={`px-2 py-1 rounded text-xs ${server.status === 'online' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                        }`}>
                        {server.status}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-white/30 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </div>

                  {/* Quick Stats Panel */}
                  <div
                    className="overflow-hidden transition-all duration-300 ease-in-out"
                    style={{
                      maxHeight: isExpanded ? '200px' : '0px',
                      opacity: isExpanded ? 1 : 0
                    }}
                  >
                    <div className="px-5 py-4 bg-white/[0.01] border-t border-white/5">
                      {isLoadingThisStats ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
                        </div>
                      ) : quickStats ? (
                        <div className="grid grid-cols-4 gap-4">
                          <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10">
                            <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                              <Zap className="w-3 h-3" />
                              Activations
                            </div>
                            <p className="text-xl font-semibold text-white">{quickStats.totalActivations}</p>
                          </div>
                          <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10">
                            <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                              <Activity className="w-3 h-3" />
                              Active
                            </div>
                            <p className="text-xl font-semibold text-green-400">{quickStats.activePages}</p>
                          </div>
                          <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10">
                            <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                              <Trash className="w-3 h-3" />
                              Cleaned
                            </div>
                            <p className="text-xl font-semibold text-white">{quickStats.cleanedFiles}</p>
                          </div>
                          <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10">
                            <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                              <Clock className="w-3 h-3" />
                              Cleanup
                            </div>
                            <p className="text-sm font-medium text-white/80">
                              {quickStats.lastCleanup ? new Date(quickStats.lastCleanup).toLocaleDateString() : 'Never'}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-center text-white/40 text-sm py-2">No stats available</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPanel;